import type { ScoringConfig, ScoreWorkoutInput } from './types';
import { DOTS_COEFFICIENTS } from './dots';
import { CATEGORY_BENCHMARKS_LB, REFERENCE_BODY_WEIGHT_LB } from './categoryPoints';

export const SCORING_VERSION = 'swol-score-v1';

export const DEFAULT_SCORING_CONFIG: ScoringConfig = {
  version: SCORING_VERSION,
  oneRepMaxFormula: 'Epley',
  maxReps: 10,
  referenceBodyWeightLb: REFERENCE_BODY_WEIGHT_LB,
  categoryBenchmarksLb: CATEGORY_BENCHMARKS_LB,
  dotsCoefficients: DOTS_COEFFICIENTS,
};

/**
 * Validates a scoring input object.
 *
 * @param input The scoring input to validate.
 * @throws Error with a descriptive message if invalid.
 */
export function validateScoringInput(input: ScoreWorkoutInput): void {
  const isBodyWeightLift = (input.bodyWeightFactor ?? 0) > 0;
  // Bodyweight lifts may be logged with 0 added weight.
  if (
    !Number.isInteger(input.enteredWeightLb) ||
    (isBodyWeightLift ? input.enteredWeightLb < 0 : input.enteredWeightLb <= 0)
  ) {
    throw new Error(
      isBodyWeightLift
        ? 'Added weight must be a non-negative integer.'
        : 'Entered weight must be a positive integer.',
    );
  }
  if (
    !Number.isInteger(input.reps) ||
    input.reps < 1 ||
    input.reps > DEFAULT_SCORING_CONFIG.maxReps
  ) {
    throw new Error(`Reps must be an integer between 1 and ${DEFAULT_SCORING_CONFIG.maxReps}.`);
  }
  if (input.bodyWeightLb <= 0) {
    throw new Error('Body weight must be a positive number.');
  }
  if (input.inputWeightMultiplier <= 0) {
    throw new Error('Input weight multiplier must be a positive number.');
  }
  if (input.normalizationMultiplier <= 0) {
    throw new Error('Normalization multiplier must be a positive number.');
  }
  if (input.scoringSex !== 'male' && input.scoringSex !== 'female') {
    throw new Error("Scoring sex must be 'male' or 'female'.");
  }
  if (!['push', 'row', 'hinge', 'squat'].includes(input.category)) {
    throw new Error("Category must be 'push', 'row', 'hinge', or 'squat'.");
  }
}
